import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import type { HiDefRenderingVersion } from 'src/shared/graphql/generated';
import { useGetRenderingLazyQuery } from 'src/shared/graphql/generated';
import { useMode } from 'src/shared/hooks';

import { Mode } from '../ModeProvider';

import { InteractiveRenderingDataProvider } from './InteractiveRenderingDataProvider';
import {
  InteractiveRenderingStateContext,
  InteractiveRenderingStateProvider,
} from './InteractiveRenderingState';

const findRenderingFile = (
  renderingVersions: HiDefRenderingVersion[],
  fileId: string,
) =>
  renderingVersions
    ?.flatMap(version => version.hiDefRenderingFiles)
    .find(file => file.id === parseInt(fileId, 10));

const InteractiveRenderingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const mode = useMode();
  const [loadError, setLoadError] = useState<Error | undefined>(undefined);
  const [renderingFileNotFound, setRenderingFileNotFound] = useState(false);

  // clickedRegion is used to distinguish between changes in selectedRegion via programmatic interaction (e.g. calls
  // to deselectAllRegions, or selectRegion) vs changes in selectedRegion caused by click events on the canvas. There
  // are effects that should only respond to click events on the canvas. An object is stored rather than a string to
  // ensure the identity of the clickedRegion changes on every click, even if the same region is clicked more than once.
  // If we stored a string instead only the first click on the  same region would change this state
  const [clickedRegion, setClickedRegion] = useState({ regionId: '' });

  const { renderingId, renderingFileId } = useParams<{
    renderingId: string;
    renderingFileId: string;
  }>();

  const [
    getRendering,
    {
      data: getRenderingData,
      loading: getRenderingLoading,
      error: getRenderingError,
    },
  ] = useGetRenderingLazyQuery();

  const renderingFile = findRenderingFile(
    getRenderingData?.getHiDefRendering?.versions as HiDefRenderingVersion[],
    renderingFileId,
  );

  useEffect(() => {
    if (mode !== Mode.INTERACTIVE_RENDERING) {
      return;
    }
    if (!getRenderingData && !getRenderingLoading && !getRenderingError) {
      void getRendering({ variables: { id: renderingId } });
      return;
    }
    if (!getRenderingLoading && !renderingFile) {
      setRenderingFileNotFound(true);
    }
  }, [
    getRendering,
    getRenderingData,
    getRenderingError,
    getRenderingLoading,
    mode,
    renderingFile,
    renderingId,
  ]);

  if (mode === Mode.INTERACTIVE_RENDERING) {
    return (
      <InteractiveRenderingDataProvider
        onClick={regionId => {
          setClickedRegion({ regionId });
        }}
        onLoadError={setLoadError}
        renderingFile={renderingFile}
      >
        <InteractiveRenderingStateProvider
          clickedRegion={clickedRegion}
          loadError={loadError}
          renderingFile={renderingFile}
          renderingFileNotFound={renderingFileNotFound}
          renderingName={getRenderingData?.getHiDefRendering?.name ?? ''}
        >
          {children}
        </InteractiveRenderingStateProvider>
      </InteractiveRenderingDataProvider>
    );
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export { InteractiveRenderingProvider, InteractiveRenderingStateContext };
